import { motion } from 'framer-motion';
import { Phone, MapPin, Building2 } from 'lucide-react';
import { StarfieldScene } from '@/components/three/StarfieldScene';
import { SplitLines } from '@/components/SplitLines';
import { BRAND } from '@/data/content';
import { usePerfTier } from '@/lib/usePerfTier';

export const CTA = () => {
  const { isMobile, dpr } = usePerfTier();

  return (
    <section id="contact" data-testid="cta-section" className="relative bg-void overflow-hidden border-t border-ink/10">
      <div className="absolute inset-0">
        <StarfieldScene dpr={dpr} count={isMobile ? 180 : 420} />
      </div>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_60%_50%_at_50%_100%,rgba(30,58,123,0.08),transparent_70%)]" />

      <div className="relative z-10 section-pad py-32 md:py-44">
        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-6">05 — Contact</p>

        <h2 className="font-clash font-semibold text-ink tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
          <SplitLines
            inView
            lines={["Let's Build the", <span key="f" className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold to-brand-royal">Future Together</span>]}
          />
        </h2>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-7 max-w-xl text-sm md:text-base text-ink/70 leading-relaxed"
        >
          Whether you're searching for your dream home, a premium investment opportunity
          or a strategic land partnership — our team is ready to assist you every step of the way.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <motion.a
            data-testid="cta-invest-btn"
            href={`tel:${BRAND.phones[0].replace(/\s/g, '')}`}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            className="bg-brand-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_35px_rgba(201,151,27,0.45)] transition-shadow duration-300"
          >
            Start Your Investment
          </motion.a>
          <motion.a
            data-testid="cta-consultation-btn"
            href={`tel:${BRAND.phones[1].replace(/\s/g, '')}`}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            className="glass text-ink text-sm font-medium px-8 py-4 rounded-full hover:border-brand-royal/60 transition-colors duration-300"
          >
            Schedule Consultation
          </motion.a>
        </motion.div>

        {/* glass contact panels */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl">
          {[
            {
              icon: Phone,
              title: 'Call Us',
              testid: 'contact-phone-panel',
              body: (
                <div className="space-y-1.5">
                  {BRAND.phones.map((ph) => (
                    <a key={ph} href={`tel:${ph.replace(/\s/g, '')}`} className="block text-sm text-ink/80 hover:text-brand-gold transition-colors duration-300">{ph}</a>
                  ))}
                </div>
              ),
            },
            {
              icon: MapPin,
              title: 'Corporate Office',
              testid: 'contact-address-panel',
              body: (
                <div className="space-y-0.5">
                  {BRAND.address.map((l) => (
                    <p key={l} className="text-sm text-ink/80">{l}</p>
                  ))}
                </div>
              ),
            },
            {
              icon: Building2,
              title: 'Our Entities',
              testid: 'contact-entities-panel',
              body: (
                <div className="mt-2 space-y-4">
                  <div className="flex flex-col gap-1.5">
                    <img src="/assets/logo.png" alt={BRAND.legal} className="h-9 object-contain object-left mix-blend-multiply" />
                    <p className="text-xs text-ink/60 font-medium">{BRAND.legal}</p>
                  </div>
                  <div className="w-full h-px bg-ink/5" />
                  <div className="flex flex-col gap-1.5">
                    <img src="/assets/sas-removebg-preview.png" alt={BRAND.partner} className="h-9 object-contain object-left mix-blend-multiply" />
                    <p className="text-xs text-ink/60 font-medium">{BRAND.partner}</p>
                  </div>
                </div>
              ),
            },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={c.testid}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-8%' }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="glass rounded-2xl p-7 hover:border-brand-gold/40 transition-colors duration-500"
            >
              <c.icon size={18} className="text-brand-gold" />
              <h3 className="mt-4 font-clash font-medium text-lg text-ink">{c.title}</h3>
              <div className="mt-3">{c.body}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
